import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { getTranslation } from '../../../utils/translations';

const LanguageManagementSection = ({ language }) => {
  const t = getTranslation(language);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('languages')?.select('*')?.order('language_name', { ascending: true });
      
      if (error) throw error;
      
      setLanguages(data?.map(lang => ({
        id: lang?.id,
        languageCode: lang?.language_code,
        languageName: lang?.language_name,
        isActive: lang?.is_active,
        createdAt: lang?.created_at
      })) || []);
    } catch (error) {
      console.error('Error fetching languages:', error);
      setLanguages([]);
    } finally {
      setLoading(false);
    }
  };

  const getLanguageFlag = (code) => {
    const flags = {
      'FR': '🇫🇷',
      'EN': '🇬🇧',
      'PL': '🇵🇱',
      'DE': '🇩🇪',
      'ES': '🇪🇸'
    };
    return flags?.[code] || '🌍';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {languages?.map((lang) => (
          <div
            key={lang?.id}
            className="bg-background border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-4xl">{getLanguageFlag(lang?.languageCode)}</span>
                <div>
                  <h3 className="font-semibold text-foreground">{lang?.languageName}</h3>
                  <p className="text-sm text-muted-foreground">{lang?.languageCode}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                lang?.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {lang?.isActive ? (t?.common?.active || 'Active') : (t?.common?.inactive || 'Inactive')}
              </span>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="Check" size={16} className="text-green-600" />
                <span>
                  {language === 'FR' && 'Interface traduite'}
                  {language === 'EN' && 'Interface translated'}
                  {language === 'PL' && 'Interfejs przetłumaczony'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="Check" size={16} className="text-green-600" />
                <span>
                  {language === 'FR' && 'Support client disponible'}
                  {language === 'EN' && 'Customer support available'}
                  {language === 'PL' && 'Obsługa klienta dostępna'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="Check" size={16} className="text-green-600" />
                <span>
                  {language === 'FR' && 'Documentation complète'}
                  {language === 'EN' && 'Complete documentation'}
                  {language === 'PL' && 'Pełna dokumentacja'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {languages?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Languages" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t?.common?.noData || 'No data available'}</p>
        </div>
      )}
    </div>
  );
};

export default LanguageManagementSection;